import { useEffect, useMemo, useRef, useState } from 'react';
import { Firestore, collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { Fund, FundDistribution, ManagementOption, PortfolioEntry } from '../../types';
import { CatalogueLanguage } from './catalogue_copy';

/** The state that means "sellable to investors today". */
export const CATALOGUE_STATE = 'ready_for_investors';

/**
 * An unset `fund_state` / `state` means "no restriction recorded", NOT "restricted".
 *
 * This is the convention the rest of the portal already follows — see the
 * "backwards compatibility. If the fund_state is not set, show the button anyway by
 * default" guard in `add_order.tsx`, and the `if (data.fund_state && ...)` check beside
 * it. Funds predating the field simply never had it written.
 *
 * Testing it with strict equality (`state === 'ready_for_investors'`) silently hides
 * every legacy product, which is how the catalogue ended up showing only the two newest
 * funds.
 */
export const isAvailableState = (state?: string | null): boolean =>
    !state || state === CATALOGUE_STATE;

/** How many published NAVs the card sparkline shows. */
export const PERFORMANCE_POINTS = 18;

export type PortfolioProduct = PortfolioEntry & { id: string };

/**
 * A fund carrying the Firestore document id it was loaded under.
 *
 * `fundsMap` is keyed by document id, but the `Fund` payload's own `id` field is document
 * *data* and is not guaranteed to match — which is why `funds_list.tsx` reaches for
 * `fund.code || String(fund.id)` rather than trusting `id`. Every Firestore path and
 * callable argument in the catalogue uses `docId`, so a fund whose data `id` disagrees
 * with its document id still reads and writes the right document.
 */
export type CatalogueFund = Fund & { docId: string };

export type CatalogueSort = 'name' | 'ytd' | 'nav' | 'dividends';

export interface CatalogueFilters {
    search: string;
    fundType: string | null;
    currency: string | null;
    assetClass: string | null;
    /**
     * null = every product. 'active' means `fund_state === 'ready_for_investors'`,
     * which is what the business calls an active product; 'inactive' is its complement.
     * State is never hidden by default — it is shown as a badge and ordered first.
     */
    activity: string | null;
    sort: CatalogueSort;
}

export const EMPTY_FILTERS: CatalogueFilters = {
    search: '',
    fundType: null,
    currency: null,
    assetClass: null,
    activity: null,
    sort: 'name',
};

export const hasActiveFilters = (filters: CatalogueFilters): boolean =>
    !!filters.search.trim() || !!filters.fundType || !!filters.currency
    || !!filters.assetClass || !!filters.activity;

/**
 * Arabic locale with Latin digits.
 *
 * `ar-KW` defaults to Arabic-Indic numerals (٤٥٦), which is correct for prose but a
 * readability regression for NAVs and fee percentages that RMs cross-check against
 * factsheets and Geneva. The `-u-nu-latn` extension keeps the Arabic month/day names
 * and RTL layout while leaving the figures in Latin digits.
 */
export const localeFor = (language: CatalogueLanguage): string =>
    language === 'ar' ? 'ar-KW-u-nu-latn' : 'en-GB';

type DateLike = { toDate?: () => Date } | Date | string | number | null | undefined;

export const toDate = (value: DateLike): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = typeof value.toDate === 'function' ? value.toDate() : null;
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

export const formatDate = (value: DateLike, language: CatalogueLanguage): string => {
    const date = toDate(value);
    if (!date) return '—';
    return date.toLocaleDateString(localeFor(language), { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatDateTime = (value: DateLike, language: CatalogueLanguage): string => {
    const date = toDate(value);
    if (!date) return '—';
    return date.toLocaleString(localeFor(language), {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

/**
 * Coerces a Firestore value to a finite number, or null when there is genuinely no value.
 *
 * The types say `number`, but the documents are older than the types: percentages, fees
 * and NAVs written by earlier tooling or imports can arrive as strings. `Number.isFinite`
 * does not coerce, so `"5"` used to render as an em-dash — a fund with real dividends
 * looked like a fund with none. Empty string / null / undefined stay missing, so a blank
 * field is never silently reported as 0.
 */
const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const formatNumber = (
    value: unknown,
    language: CatalogueLanguage,
    options?: Intl.NumberFormatOptions,
): string => {
    const parsed = toFiniteNumber(value);
    return parsed === null ? '—' : parsed.toLocaleString(localeFor(language), options);
};

/** NAVs are quoted to 5 decimals in Geneva; trailing zeros are noise on a card. */
export const formatNav = (value: unknown, language: CatalogueLanguage): string =>
    formatNumber(value, language, { minimumFractionDigits: 3, maximumFractionDigits: 5 });

export const formatPercent = (value: unknown, language: CatalogueLanguage): string => {
    const parsed = toFiniteNumber(value);
    return parsed === null ? '—' : `${formatNumber(parsed, language, { maximumFractionDigits: 2 })}%`;
};

export const formatAmount = (
    value: unknown,
    language: CatalogueLanguage,
    currency?: string,
): string => {
    const parsed = toFiniteNumber(value);
    if (parsed === null) return '—';
    const amount = formatNumber(parsed, language, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
    return currency ? `${amount} ${currency}` : amount;
};

/**
 * Closed-ended (private) funds are not marked to market, so they carry no NAV and no NAV
 * history — a NAV chart for one is an empty box. What they do publish is distributions,
 * so that is what the catalogue shows for them instead.
 */
export const isClosedEnded = (fund: Fund): boolean => fund.type === 'closed_ended';

/** Milliseconds for a distribution date, tolerating Timestamp / Date / string / epoch shapes. */
const dividendMillis = (dividend: FundDistribution): number => toDate(dividend?.date as any)?.getTime() || 0;

/**
 * Distributions newest first.
 *
 * Source is the `funds/{id}/dividends` subcollection, which assigned fund managers
 * maintain. Non-array input is tolerated rather than thrown on, so one malformed fund
 * cannot blank the whole grid.
 */
export const distributionsNewestFirst = (dividends?: FundDistribution[]): FundDistribution[] =>
    (Array.isArray(dividends) ? [...dividends] : [])
        .filter(Boolean)
        .sort((a, b) => dividendMillis(b) - dividendMillis(a));

/**
 * The note on a distribution in the requested language.
 *
 * Arabic is optional when a manager records a distribution, so the Arabic catalogue
 * falls back to the English note rather than showing nothing.
 */
export const distributionNotes = (dividend: FundDistribution | null | undefined, language: CatalogueLanguage): string => {
    if (!dividend) return '';
    const en = `${dividend.notes || ''}`.trim();
    const ar = `${dividend.notes_ar || ''}`.trim();
    return (language === 'ar' ? ar || en : en || ar);
};

/** Return-of-capital entries hand money back; everything else (PNL, legacy dividends) is profit. */
const isReturnOfCapital = (dividend: FundDistribution): boolean =>
    dividend.type === 'distribution' && dividend.distribution_type === 'roc';

/**
 * The most recent PNL distribution, or null when the fund has never paid one.
 *
 * "Latest distribution" on the card and in the drawer means the latest profit paid out,
 * so return-of-capital entries are skipped: a recent capital redemption must not hide
 * the last real distribution. Legacy `dividend` records carry no kind and were always
 * profit distributions, so they count as PNL.
 */
export const latestDistribution = (dividends?: FundDistribution[]): FundDistribution | null =>
    distributionsNewestFirst(dividends).find((dividend) => !isReturnOfCapital(dividend)) || null;

/** Sum of every declared distribution percentage. */
export const totalDistributedPercent = (dividends?: FundDistribution[]): number =>
    distributionsNewestFirst(dividends).reduce((total, dividend) => total + (Number(dividend.percentage) || 0), 0);

/**
 * Sum of return-of-capital distributions only.
 *
 * Closed-ended cards quote how much capital has come back to investors, so PNL
 * distributions and legacy `dividend` records (which carry no kind) are excluded.
 */
export const totalReturnOfCapitalPercent = (dividends?: FundDistribution[]): number =>
    distributionsNewestFirst(dividends)
        .filter(isReturnOfCapital)
        .reduce((total, dividend) => total + (Number(dividend.percentage) || 0), 0);

/** How many distributions the fund has on record. */
export const distributionCount = (dividends?: FundDistribution[]): number =>
    distributionsNewestFirst(dividends).length;

/**
 * Distributions oldest first, for the closed-ended chart. Entries without a usable
 * percentage are dropped rather than plotted as zero-height bars.
 */
export const distributionSeries = (dividends?: FundDistribution[]): { percentage: number; date: any; millis: number }[] =>
    distributionsNewestFirst(dividends)
        .filter((dividend) => Number.isFinite(Number(dividend.percentage)))
        .map((dividend) => ({
            percentage: Number(dividend.percentage),
            date: dividend.date,
            millis: dividendMillis(dividend),
        }))
        .sort((a, b) => a.millis - b.millis);

/**
 * Distributions per fund, read from each fund's `dividends` subcollection.
 *
 * Same fan-out shape as `useFundPerformance`: one query per fund, cached by document id,
 * settled individually so a single failure cannot blank every card.
 */
export const useFundDistributions = (
    db: Firestore,
    funds: CatalogueFund[],
): Record<string, FundDistribution[]> => {
    const [distributions, setDistributions] = useState<Record<string, FundDistribution[]>>({});
    const requested = useRef(new Set<string>());
    const fundIds = useMemo(() => funds.map((fund) => fund.docId).sort().join(','), [funds]);

    useEffect(() => {
        const pending = funds.filter((fund) => !requested.current.has(fund.docId));
        if (!pending.length) return;
        pending.forEach((fund) => requested.current.add(fund.docId));

        let cancelled = false;
        Promise.allSettled(pending.map(async (fund) => {
            // `updates` also holds letters and announcements; filtering by type keeps the
            // announcement HTML out of a query that runs once per fund on page load.
            // Equality alone needs only the automatic single-field index.
            const snapshot = await getDocs(query(
                collection(db, 'funds', fund.docId, 'updates'),
                where('type', 'in', ['dividend', 'distribution']),
            ));
            return [
                fund.docId,
                snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as FundDistribution)),
            ] as const;
        })).then((results) => {
            if (cancelled) return;
            setDistributions((current) => {
                const next = { ...current };
                results.forEach((result, index) => {
                    if (result.status === 'fulfilled') next[result.value[0]] = result.value[1];
                    // Allow a retry on the next mount rather than caching the failure.
                    else requested.current.delete(pending[index].docId);
                });
                return next;
            });
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, fundIds]);

    return distributions;
};

export const fundDisplayName = (fund: Fund, language: CatalogueLanguage): string =>
    (language === 'ar' ? fund.name_ar : fund.name_en) || fund.name || fund.name_en || fund.name_ar || '';

export const fundSecondaryName = (fund: Fund, language: CatalogueLanguage): string =>
    (language === 'ar' ? fund.name_en : fund.name_ar) || '';

export const portfolioDisplayName = (portfolio: PortfolioProduct, language: CatalogueLanguage): string =>
    (language === 'ar' ? portfolio.name_ar : portfolio.name_en) || portfolio.name_en || portfolio.name_ar || portfolio.id;

export const portfolioDescription = (portfolio: PortfolioProduct, language: CatalogueLanguage): string =>
    (language === 'ar' ? portfolio.description_ar : portfolio.description_en) || '';

export const managementOptionName = (option: ManagementOption, language: CatalogueLanguage): string =>
    (language === 'ar' ? option.name_ar : option.name_en) || option.portfolio_type || option.id;

export const managementOptionDescription = (option: ManagementOption, language: CatalogueLanguage): string =>
    (language === 'ar' ? option.description_ar : option.description_en) || '';

/** Every field a product can be matched on, flattened once so filtering stays cheap. */
const fundSearchIndex = (fund: Fund): string =>
    [fund.name_en, fund.name_ar, fund.name, fund.short_name, fund.type, fund.currency, ...(fund.asset_class || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

const portfolioSearchIndex = (portfolio: PortfolioProduct): string =>
    [
        portfolio.name_en, portfolio.name_ar, portfolio.description_en, portfolio.description_ar, portfolio.portfolio_type,
        ...(portfolio.management_options || []).flatMap((option) => [option.name_en, option.name_ar, option.portfolio_type, option.asset_class]),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

/**
 * Real funds only — every state, but no placeholder documents.
 *
 * Two guards, both matching what the operations `/funds` page does via
 * `shouldShowFundInList`:
 *
 *  - a usable `id`, since scratch/dummy documents in the collection have none;
 *  - a real fund `type`, which is the check that keeps those documents off `/funds`.
 *
 * `fund_state` is deliberately NOT filtered here: availability is shown as a badge, drives
 * the default ordering, and can be narrowed with the availability filter — so a legacy
 * fund with no state recorded is still findable.
 */
export const selectCatalogueFunds = (fundsMap: Record<string, Fund> | undefined): CatalogueFund[] =>
    Object.entries(fundsMap || {})
        .filter(([, fund]) => !!fund
            && `${fund.id ?? ''}`.trim().length > 0
            && (fund.type === 'open_ended' || fund.type === 'closed_ended'))
        .map(([docId, fund]) => ({ ...fund, docId }));

export const selectCataloguePortfolios = (
    portfolioService: Record<string, PortfolioEntry> | undefined,
): PortfolioProduct[] =>
    Object.entries(portfolioService || {})
        .filter(([, portfolio]) => portfolio && isAvailableState(portfolio.state))
        .map(([id, portfolio]) => ({ ...portfolio, id }))
        .sort((a, b) => (a.position || 0) - (b.position || 0));

/** Management options a client can actually be sold today. */
export const activeManagementOptions = (portfolio: PortfolioProduct): ManagementOption[] =>
    (portfolio.management_options || []).filter((option) => option.active !== false && isAvailableState(option.state));

/**
 * The entry ticket for a portfolio service: the smallest minimum amount across its active
 * management options, with that option's currency. The portfolio-level `minimum_amount`
 * is not used — the business maintains minimums per option. Null when no option has one,
 * so callers show a dash rather than a misleading zero.
 */
export const portfolioMinimumAmount = (portfolio: PortfolioProduct): { amount: number; currency?: string } | null =>
    activeManagementOptions(portfolio)
        .map((option) => ({ amount: Number(option.minimum_amount?.amount), currency: option.minimum_amount?.currency }))
        .filter((entry) => Number.isFinite(entry.amount) && entry.amount > 0)
        .sort((a, b) => a.amount - b.amount)[0] || null;

/**
 * Sellable products come first, whatever the chosen sort.
 *
 * Three tiers rather than two: an explicit `ready_for_investors` outranks a fund that
 * simply never had a state recorded, which in turn outranks anything explicitly
 * restricted (internal_only, closed_to_new_investors, …).
 */
const availabilityRank = (fund: Fund): number => {
    if (fund.fund_state === CATALOGUE_STATE) return 0;
    if (!fund.fund_state) return 1;
    return 2;
};

const compareByName = (a: Fund, b: Fund, language: CatalogueLanguage) =>
    fundDisplayName(a, language).localeCompare(fundDisplayName(b, language), localeFor(language));

/** Missing metrics sort last in every direction rather than being treated as zero. */
const compareNumericDescending = (a: number | undefined, b: number | undefined) => {
    const left = Number.isFinite(a) ? Number(a) : Number.NEGATIVE_INFINITY;
    const right = Number.isFinite(b) ? Number(b) : Number.NEGATIVE_INFINITY;
    return right - left;
};

/**
 * @param distributions Per-fund distributions from `useFundDistributions`, keyed by
 * document id. Required only for the "dividends" sort — distributions live in the
 * `updates` subcollection now, so a fund document no longer carries a count to sort on.
 */
export const filterFunds = (
    funds: CatalogueFund[],
    filters: CatalogueFilters,
    language: CatalogueLanguage,
    distributions?: Record<string, FundDistribution[]>,
): CatalogueFund[] => {
    const term = filters.search.trim().toLowerCase();
    const matched = funds.filter((fund) => {
        if (term && !fundSearchIndex(fund).includes(term)) return false;
        if (filters.fundType && fund.type !== filters.fundType) return false;
        if (filters.currency && fund.currency !== filters.currency) return false;
        if (filters.assetClass && !(fund.asset_class || []).some((item) => String(item) === filters.assetClass)) return false;
        if (filters.activity === 'active' && fund.fund_state !== CATALOGUE_STATE) return false;
        if (filters.activity === 'inactive' && fund.fund_state === CATALOGUE_STATE) return false;
        return true;
    });

    return [...matched].sort((a, b) => {
        // Availability leads regardless of the chosen sort: an RM scanning the grid should
        // reach the products they can actually sell without scrolling past retired ones.
        const byAvailability = availabilityRank(a) - availabilityRank(b);
        if (byAvailability !== 0) return byAvailability;
        switch (filters.sort) {
            case 'ytd': return compareNumericDescending(a.ytd, b.ytd) || compareByName(a, b, language);
            case 'nav': return compareNumericDescending(a.nav_per_unit, b.nav_per_unit) || compareByName(a, b, language);
            case 'dividends': return compareNumericDescending(
                distributions?.[a.docId]?.length,
                distributions?.[b.docId]?.length,
            ) || compareByName(a, b, language);
            default: return compareByName(a, b, language);
        }
    });
};

export const filterPortfolios = (portfolios: PortfolioProduct[], filters: CatalogueFilters): PortfolioProduct[] => {
    const term = filters.search.trim().toLowerCase();
    return portfolios.filter((portfolio) => {
        if (term && !portfolioSearchIndex(portfolio).includes(term)) return false;
        // Fund-only facets never exclude a portfolio, but currency does when the
        // portfolio publishes allowed currencies on its management options.
        if (filters.currency) {
            const currencies = (portfolio.management_options || [])
                .flatMap((option) => (option.allowed_currencies || []).map((entry) => entry.currency));
            if (currencies.length && !currencies.includes(filters.currency)) return false;
        }
        return true;
    });
};

/** Distinct, sorted facet values, so the filter pills only offer what exists. */
export const collectFacets = (funds: CatalogueFund[]) => ({
    currencies: [...new Set(funds.map((fund) => fund.currency).filter(Boolean))].sort(),
    assetClasses: [...new Set(funds.flatMap((fund) => (fund.asset_class || []).map(String)).filter(Boolean))].sort(),
});

/** The single-value facets a filter pill can toggle. `search` and `sort` are not pills. */
export type FacetKey = 'fundType' | 'currency' | 'assetClass' | 'activity';

export interface FacetCounts {
    /** Funds left when this facet is cleared but every other filter still applies. */
    all: number;
    /** Funds left for each candidate value of this facet, other filters still applied. */
    byValue: Record<string, number>;
}

/**
 * How many funds each value of one facet would leave on screen.
 *
 * Every *other* filter stays applied, but the facet's own current value is ignored, so
 * the alternatives in a pill row never read as zero just because a sibling is selected.
 * A pill whose count is zero is a dead end and the bar disables it, which is what makes
 * the pills a lookup aid rather than a second copy of the dropdowns.
 */
export const countFacetMatches = (
    funds: CatalogueFund[],
    filters: CatalogueFilters,
    key: FacetKey,
    values: string[],
    language: CatalogueLanguage,
    distributions?: Record<string, FundDistribution[]>,
): FacetCounts => {
    const base = filterFunds(funds, { ...filters, [key]: null }, language, distributions);
    return {
        all: base.length,
        byValue: Object.fromEntries(values.map((value) => [
            value,
            filterFunds(base, { ...EMPTY_FILTERS, [key]: value }, language, distributions).length,
        ])),
    };
};

export interface FundPerformance {
    values: number[];
    loading: boolean;
    failed: boolean;
}

/**
 * Latest published NAVs per fund, for the card sparklines.
 *
 * Fetched once per fund and cached in a ref: the previous implementation re-ran the
 * whole fan-out whenever the `funds` array identity changed, which is on every
 * `fundsMap` snapshot — N history queries per NAV update, portal-wide.
 *
 * One fund failing (missing history, rules) must not blank every other sparkline, so
 * results are settled individually.
 */
export const useFundPerformance = (db: Firestore, funds: CatalogueFund[]): Record<string, FundPerformance> => {
    const [performance, setPerformance] = useState<Record<string, FundPerformance>>({});
    const requested = useRef(new Set<string>());
    const fundIds = useMemo(
        () => funds.filter((fund) => !isClosedEnded(fund)).map((fund) => fund.docId).sort().join(','),
        [funds],
    );

    useEffect(() => {
        // Closed-ended funds publish no NAV, so querying their history is a guaranteed
        // empty read per fund per page load.
        const pending = funds.filter((fund) => !isClosedEnded(fund) && !requested.current.has(fund.docId));
        if (!pending.length) return;
        pending.forEach((fund) => requested.current.add(fund.docId));

        let cancelled = false;
        setPerformance((current) => {
            const next = { ...current };
            pending.forEach((fund) => { next[fund.docId] = { values: [], loading: true, failed: false }; });
            return next;
        });

        Promise.allSettled(pending.map(async (fund) => {
            const snapshot = await getDocs(query(
                collection(db, 'funds', fund.docId, 'history'),
                orderBy('nav_date', 'desc'),
                limit(PERFORMANCE_POINTS),
            ));
            const values = snapshot.docs
                .map((item) => item.data().nav_per_unit)
                .filter((value): value is number => Number.isFinite(value))
                .reverse();
            // The fund doc carries the newest NAV before the history doc is written.
            if (Number.isFinite(fund.nav_per_unit) && values[values.length - 1] !== fund.nav_per_unit) {
                values.push(Number(fund.nav_per_unit));
            }
            return [fund.docId, values] as const;
        })).then((results) => {
            if (cancelled) return;
            setPerformance((current) => {
                const next = { ...current };
                results.forEach((result, index) => {
                    const fundId = pending[index].docId;
                    if (result.status === 'fulfilled') {
                        next[fundId] = { values: result.value[1], loading: false, failed: false };
                    } else {
                        // Allow a retry on the next mount rather than caching the failure forever.
                        requested.current.delete(fundId);
                        next[fundId] = { values: [], loading: false, failed: true };
                    }
                });
                return next;
            });
        });

        return () => { cancelled = true; };
        // `fundIds` is the stable identity; `funds` itself is a fresh array each snapshot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, fundIds]);

    return performance;
};
